import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Section, SectionHeader } from "@/components/site/Section";
import { relationshipLabels } from "@/content/review";
import { createCycle, listMyCycles, type CycleSummary } from "@/utils/review.functions";
import { supabase } from "@/integrations/supabase/client";
import { useSession } from "@/hooks/useSession";

export const Route = createFileRoute("/_authenticated/my-review/")({
  head: () => ({
    meta: [
      { title: "Your 360 checks | Leadership Edge Live" },
      {
        name: "description",
        content: "Start a 360 Leader Behavior Check and follow how many people have answered.",
      },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Your 360 checks" },
      { property: "og:description", content: "Start and follow your 360 Leader Behavior Checks." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: MyReviewsPage,
});

function MyReviewsPage() {
  const load = useServerFn(listMyCycles);
  const start = useServerFn(createCycle);
  const navigate = useNavigate();
  const { user } = useSession();

  const [cycles, setCycles] = useState<CycleSummary[] | null>(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    load()
      .then(setCycles)
      .catch(() => setError("We couldn't load your checks right now."));
  }, [load]);

  function begin() {
    setBusy(true);
    setError(null);
    start({ data: { leaderName: name } })
      .then(({ cycleId }) => navigate({ to: "/my-review/$cycleId/", params: { cycleId } }))
      .catch(() => setError("We couldn't start a check right now."))
      .finally(() => setBusy(false));
  }

  async function signOut() {
    await supabase.auth.signOut().catch(() => undefined);
    navigate({ to: "/begin-360-check/", replace: true });
  }

  return (
    <Section>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-[6px] border border-border/60 bg-lavender/40 px-4 py-3 text-sm">
        <span className="text-muted-foreground">
          Signed in as <span className="font-semibold text-ink">{user?.email ?? "..."}</span>
        </span>
        <button
          type="button"
          onClick={signOut}
          className="min-h-11 rounded-[6px] border border-primary px-4 py-2 font-semibold text-primary"
        >
          Sign out
        </button>
      </div>
      <SectionHeader
        eyebrow="360 Leader Behavior Check"
        title="Your checks"
        intro="Start a check, rate yourself, then send private links to the people who work with you."
        titleClassName="text-3xl font-semibold leading-tight text-ink md:text-4xl"
      />


      <div className="rounded-2xl border border-border/60 bg-background p-7">
        <h3 className="font-display text-xl font-semibold text-ink">Start a new check</h3>
        <p className="mt-2 text-sm text-muted-foreground">
          The name is what raters see, so use the name they know the leader by.
        </p>
        <div className="mt-5 flex flex-col gap-3 sm:flex-row">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Name of the leader being rated"
            className="flex-1 rounded-[6px] border border-border bg-background px-4 py-3 text-sm text-ink"
          />
          <button
            type="button"
            disabled={busy}
            onClick={begin}
            className="rounded-[6px] bg-[#693D94] px-6 py-3 text-sm font-semibold text-primary-foreground shadow-brand disabled:opacity-60"
          >
            Start a check
          </button>
        </div>
        {error && <p className="mt-3 text-sm text-primary">{error}</p>}
      </div>

      <div className="mt-10 space-y-4">
        {cycles === null && <p className="text-sm text-muted-foreground">Loading your checks.</p>}
        {cycles?.length === 0 && (
          <p className="text-sm text-muted-foreground">You haven't started a check yet.</p>
        )}
        {cycles?.map((cycle) => (
          <div key={cycle.id} className="rounded-2xl border border-border/60 bg-background p-7">
            <div className="flex flex-wrap items-baseline justify-between gap-3">
              <h3 className="font-display text-xl font-semibold text-ink">
                {cycle.leaderName || "Unnamed check"}
              </h3>
              <p className="text-xs font-semibold uppercase tracking-wider text-primary">
                {cycle.status}
                {cycle.unlocked ? " · full report" : " · free preview"}
              </p>
            </div>
            <p className="mt-3 text-sm text-muted-foreground">
              {cycle.selfComplete ? "Self-rating done" : "Self-rating not done yet"} ·{" "}
              {(["direct_report", "peer", "manager"] as const)
                .map(
                  (rel) =>
                    `${relationshipLabels[rel]} ${cycle.submitted[rel]}/${cycle.invited[rel]}`,
                )
                .join(" · ")}
            </p>
            <div className="mt-5 flex flex-wrap gap-4 text-sm font-semibold text-primary">
              <Link to="/my-review/$cycleId/" params={{ cycleId: cycle.id }}>
                Manage raters
              </Link>
              <Link to="/my-review/$cycleId/results/" params={{ cycleId: cycle.id }}>
                View results
              </Link>
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}
